import React, { useState, useEffect } from 'react';
import Icon from '../ui/Icon';
import { applyAccentColor, applyFullTheme, THEME_PRESETS } from '../constants/theme';

const PRESET_COLORS = [
  { name: 'Ocean Blue',    hex: '#3B5BDB' },
  { name: 'Certis Navy',   hex: '#1E3A5F' },
  { name: 'Certis Orange', hex: '#E07B2A' },
  { name: 'Violet',        hex: '#7C3AED' },
  { name: 'Emerald',       hex: '#059669' },
  { name: 'Teal',          hex: '#0891B2' },
  { name: 'Rose',          hex: '#E11D48' },
  { name: 'Amber',         hex: '#D97706' },
  { name: 'Slate',         hex: '#475569' },
  { name: 'Indigo',        hex: '#4338CA' },
];

export default function AppearanceModal({ isOpen, onClose, theme, accentColor, themeName, onSave }) {
  const [selectedTheme,     setSelectedTheme]     = useState(theme);
  const [selectedAccent,    setSelectedAccent]    = useState(accentColor || '#3B5BDB');
  const [customColor,       setCustomColor]       = useState(accentColor || '#3B5BDB');
  const [selectedThemeName, setSelectedThemeName] = useState(themeName || 'default');

  useEffect(() => {
    if (isOpen) {
      setSelectedTheme(theme);
      setSelectedAccent(accentColor || '#3B5BDB');
      setCustomColor(accentColor || '#3B5BDB');
      setSelectedThemeName(themeName || 'default');
    }
  }, [isOpen, theme, accentColor, themeName]);

  // Live preview — apply accent instantly as user picks
  const pickColor = (hex) => {
    setSelectedAccent(hex);
    setCustomColor(hex);
    setSelectedThemeName('default'); // custom accent clears brand theme
    applyAccentColor(hex);
  };

  // Live preview for brand theme
  const pickTheme = (key) => {
    setSelectedThemeName(key);
    applyFullTheme(key);
    if (key !== 'default') {
      setSelectedAccent(THEME_PRESETS[key].accent);
      setCustomColor(THEME_PRESETS[key].accent);
    }
  };

  const handleCancel = () => {
    // revert preview
    if (themeName && themeName !== 'default') {
      applyFullTheme(themeName);
    } else {
      applyAccentColor(accentColor || '#3B5BDB');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div style={{ background: 'var(--surface)', color: 'var(--text)' }} className="rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div style={{ borderBottom: '1px solid var(--border)' }} className="flex items-center justify-between px-6 py-4">
          <h2 style={{ color: 'var(--text)' }} className="text-lg font-bold">Appearance</h2>
          <button onClick={handleCancel} style={{ color: 'var(--muted)' }} className="p-1 rounded-md hover:opacity-75 transition-colors">
            <Icon name="close" size={20} color="currentColor" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">

          {/* ── Brand Theme ── */}
          <div>
            <h3 style={{ color: 'var(--text)' }} className="text-sm font-semibold mb-1">Brand Theme</h3>
            <p style={{ color: 'var(--muted)' }} className="text-xs mb-4">Applies a coordinated colour palette across the entire application.</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(THEME_PRESETS).map(([key, preset]) => {
                const active = selectedThemeName === key;
                return (
                  <button
                    key={key}
                    onClick={() => pickTheme(key)}
                    style={{
                      border: active ? `2px solid ${preset.accent}` : '2px solid var(--border)',
                      borderRadius: 12,
                      padding: '12px 10px',
                      background: active ? `${preset.accent}18` : 'var(--bg)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      textAlign: 'left',
                    }}
                  >
                    {/* Mini palette preview */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      <div style={{ flex: 1, height: 22, borderRadius: 5, background: preset.sidebar }} />
                      <div style={{ flex: 2, height: 22, borderRadius: 5, background: preset.bg, border: '1px solid rgba(0,0,0,0.08)' }} />
                      <div style={{ width: 22, height: 22, borderRadius: 5, background: preset.accent }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${preset.accent}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: preset.accent }} />}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{preset.name}</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, marginLeft: 20 }}>
                      {key === 'default' ? 'Light • Blue accent' : 'Dark navy • Orange accent'}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Theme mode preference ── */}
          <div>
            <h3 style={{ color: 'var(--text)' }} className="text-sm font-semibold mb-4">Theme Preference</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSelectedTheme('light')}
                className={`flex flex-col items-center p-4 border-2 rounded-xl transition-all`}
                style={selectedTheme === 'light' ? { borderColor: 'var(--accent)', background: 'var(--accent-bg)' } : { borderColor: 'var(--border)' }}
              >
                <div className="w-16 h-12 bg-gray-100 rounded shadow-sm border border-gray-200 flex flex-col overflow-hidden mb-3">
                  <div className="h-3 bg-white w-full border-b border-gray-200" />
                  <div className="flex-1 w-full bg-gray-50" />
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${selectedTheme === 'light' ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedTheme === 'light' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />}
                  </div>
                  Light Mode
                </div>
              </button>
              <button
                onClick={() => setSelectedTheme('dark')}
                className={`flex flex-col items-center p-4 border-2 rounded-xl transition-all`}
                style={selectedTheme === 'dark' ? { borderColor: 'var(--accent)', background: 'var(--accent-bg)' } : { borderColor: 'var(--border)' }}
              >
                <div className="w-16 h-12 bg-gray-800 rounded shadow-sm border border-gray-700 flex flex-col overflow-hidden mb-3">
                  <div className="h-3 bg-gray-900 w-full border-b border-gray-700" />
                  <div className="flex-1 w-full bg-gray-800" />
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${selectedTheme === 'dark' ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedTheme === 'dark' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />}
                  </div>
                  Dark Mode
                </div>
              </button>
            </div>
            <p style={{ color: 'var(--muted)' }} className="mt-2 text-xs">Dark mode mockup — full implementation coming soon.</p>
          </div>

          {/* ── Accent Color ── */}
          <div>
            <h3 style={{ color: 'var(--text)' }} className="text-sm font-semibold mb-1">Accent Color</h3>
            <p style={{ color: 'var(--muted)' }} className="text-xs mb-4">Override the accent colour independently of the brand theme.</p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              {PRESET_COLORS.map(({ name, hex }) => {
                const active = hex.toLowerCase() === selectedAccent.toLowerCase() && selectedThemeName === 'default';
                return (
                  <button
                    key={hex}
                    title={name}
                    onClick={() => pickColor(hex)}
                    style={{
                      width: 36, height: 36, borderRadius: 10, background: hex,
                      border: 'none', cursor: 'pointer',
                      boxShadow: active ? `0 0 0 2px #fff, 0 0 0 4px ${hex}` : '0 1px 4px rgba(0,0,0,0.18)',
                      transform: active ? 'scale(1.18)' : 'scale(1)',
                      transition: 'all 0.15s', position: 'relative',
                    }}
                  >
                    {active && (
                      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)' }}>
              <input
                type="color"
                value={customColor}
                onChange={e => pickColor(e.target.value)}
                style={{ width: 36, height: 36, padding: 2, border: '2px solid var(--border)', borderRadius: 8, cursor: 'pointer', background: 'none', flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>Custom Color</div>
                <input
                  type="text"
                  value={customColor}
                  maxLength={7}
                  onChange={e => {
                    const v = e.target.value;
                    setCustomColor(v);
                    if (/^#[0-9A-Fa-f]{6}$/.test(v)) pickColor(v);
                  }}
                  style={{ fontSize: 12, color: 'var(--muted)', background: 'transparent', border: 'none', outline: 'none', fontFamily: 'monospace', width: '100%' }}
                  placeholder="#3B5BDB"
                />
              </div>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: selectedAccent, border: '2px solid var(--border)', flexShrink: 0 }} />
            </div>
          </div>

          {/* Live preview hint */}
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--accent-bg)', border: '1.5px solid var(--accent-light)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>Live preview — the app is reflecting your choice right now.</span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }} className="px-6 py-4 flex justify-end gap-3">
          <button onClick={handleCancel} style={{ color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--border)' }} className="px-4 py-2 text-sm font-medium rounded-md hover:opacity-80 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => { onSave(selectedTheme, selectedAccent, selectedThemeName); onClose(); }}
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
            style={{ background: 'var(--accent)' }}
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

